import { Inject, Injectable } from '@angular/core';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { CapacitorHttp } from '@capacitor/core';
import { firstValueFrom, from, lastValueFrom, Observable, throwError } from 'rxjs';
import { OidcStorageService } from '../../../oidc/storage/oidc-storage.service';
import { NetworkProvider } from '../network/network';
import { OidcSecurityService } from '../../../oidc/oidc-security.service';
import { Events } from '../events/events.provider';
import { OIDC_SERVICE_INJECTION_TOKEN } from '../../../../main';

/**
 * Requests Background Provider
 *
 * Ce service permet d'utiliser CapacitorHttp qui se base sur du code natif, il permet d'envoyer des requêtes quand l'application est en arrière plan
 * car le client HTTP d'Angular ne fonctionne pas après 5 minutes en arrière plan
 */
@Injectable({ providedIn: 'root' })
export class RequestsBackgroundProvider {
  /**
   * Logger
   */
  private logger: LoggerWrapper;

  /**
   * Constructeur
   *
   * Crée une instance de RequestsBackgroundProvider.
   *
   * @param loggerFactory
   * @param oidcStorageService
   * @param oidcSecurityService
   * @param networkProvider
   * @param events
   */
  constructor(
    private loggerFactory: LoggerFactory,
    private oidcStorageService: OidcStorageService,
    @Inject(OIDC_SERVICE_INJECTION_TOKEN) private oidcSecurityService: OidcSecurityService,
    private networkProvider: NetworkProvider,
    private events: Events
  ) {
    this.logger = this.loggerFactory.getInstance(this);
  }

  /**
   * Effectue un appel HTTP POST en utilisant l'API `CapacitorHttp` avec système de refresh token
   *
   * @param {string} url - L'URL de la requête.
   * @param {any} data donnée de la requête
   */
  async postCapacitorHttpRequest(url: string, data: any) {
    const authType = 'normal';
    let idToken = await this.oidcStorageService.getIdToken(authType);
    let response = await this.postRequestWithToken(idToken, url, data);

    if (response.status == 401 || response.status == 403) {
      lastValueFrom(this.oidcSecurityService.forceRefreshSession(authType))
        .then(async (loginResponse) => {
          this.networkProvider.addHistory('Rafrachissement du token OK', { idToken: loginResponse.idToken });
          this.logger.log('[interceptorHttp:requestError] 401/403 from APIM refreshing tokens...');
          let response = await this.postRequestWithToken(loginResponse.idToken, url, data);
          if (response.status == 400 || response.status == 401 || response.status == 403) {
            lastValueFrom(this.loginViaUi(authType))
              .then(async (idToken) => {
                await this.postRequestWithToken(idToken, url, data);
              })
              .catch((error) => throwError(() => error));
          } else {
            throwError(() => response.status);
          }
        })
        .catch((error) => {
          this.logger.log('refreshTokens catchError httpError', error);
        });
    } else {
      throwError(() => response.status);
    }
  }

  private loginViaUi(authType: 'normal') {
    return new Observable<string>((observer) => {
      this.events.subscribe('authentification:loginSuccessful', async () => {
        this.logger.log('[interceptorHttp:loginViaUI] Login via UI successful.');
        const idToken = await firstValueFrom(this.oidcSecurityService.getIdToken(authType));

        observer.next(idToken);
        observer.complete();
      });

      this.oidcSecurityService.redirectToAdfsLoginPage(authType);
    });
  }

  async postRequestWithToken(idToken: string, url: string, data: any) {
    return await CapacitorHttp.post({
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-type': 'application/json',
        Authorization: 'Bearer ' + idToken,
      },
      url: url,
      data: data,
    });
  }
}
